import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getCurrentUser, logout as logoutApi } from '@/lib/api'
import type { User } from '@/types'

// Mock user for development when backend is unavailable
const DEV_MOCK_USER: User = {
  username: 'dev_user',
  role: 'admin',
  permissions: {
    alerts: true,
    orderbook: true,
    mark_price: true,
    tags: true,
  },
}

export function useAuth() {
  const queryClient = useQueryClient()
  const isDev = import.meta.env.DEV

  const { data: user, isLoading, error } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const result = await getCurrentUser()
        console.log('Auth check result:', result)
        return result
      } catch (e) {
        console.error('Auth check failed:', e)
        throw e
      }
    },
    retry: false,
    staleTime: 60000,
    retryDelay: 5000,
  })

  const logoutMutation = useMutation({
    mutationFn: logoutApi,
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null)
      window.location.href = '/login'
    },
  })

  // Determine if backend is unavailable (network error vs auth error)
  const isNetworkError = error && (error as Error)?.message?.includes('Failed to fetch')
  
  // In dev mode, use mock user if backend is unavailable (network error)
  const effectiveUser = user || (isDev && isNetworkError ? DEV_MOCK_USER : null)
  const isAuthenticated = !!effectiveUser
  const isAdmin = effectiveUser?.role === 'admin'

  const hasPermission = (permission: string): boolean => {
    if (!effectiveUser) return false
    if (effectiveUser.role === 'admin') return true
    return !!effectiveUser.permissions?.[permission]
  }

  return {
    user: effectiveUser,
    isLoading: isLoading && !isNetworkError,
    error,
    isNetworkError,
    isAuthenticated,
    isAdmin,
    hasPermission,
    logout: logoutMutation.mutate,
    isLoggingOut: logoutMutation.isPending,
  }
}
